<template>
    <!-- Edit Diagnosis -->
    <div class="card" :class="{ 'teal lighten-5': !error, 'red lighten-5': error }" v-if="editDiagnosis">
        <div class="card-content">
            <div class="row">
                <form class="col s12">
                    <div class="row">
                        <div class="col s12">
                            <span class="card-title">Edit Diagnosis</span>
                        </div>

                        <div class="input-field col s12">
                            <input v-model="diagnosis" id="diagnosis" type="text" :disabled="isSubmitting" />
                            <label class="active" for="diagnosis">Diagnosis</label>
                        </div>

                        <div class="input-field col s12">
                            <a class="waves-effect waves-light btn black lighten-2 col s12 m2 left" :class="{ 'disabled': isSubmitting }"
                                @click.prevent="handleBack('clearDiagnosis')">
                                <i class="medium material-icons left">chevron_left</i>Back
                            </a>

                             <!-- Breaker -->
                            <span class="co1 m7"></span>

                            <button class="waves-effect waves-light btn col s12 m3 right"
                                :class="{ 'disabled': isSubmitting, 'teal': diagnosis }"
                                @click.prevent="handleDiagnosis">
                                <i class="medium material-icons left">sync</i>Confirm Edit
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Add Claim Item -->
    <div class="card add-claim-item-card" :class="{ 'teal lighten-5': !error, 'red lighten-5': error }" v-else>
        <div class="card-content">
            <div class="row">
                <form class="col s12">
                    <div class="row">
                        <div class="col s12" v-if="!isSubmitting">
                            <span class="card-title">Add Claim Item
                                <span class="secondary-content tariff-card-actions">
                                    <button type="button" class="btn-flat tariff-card-icon-action tariff-preview-top-action"
                                        title="View tariff items" @click.prevent="handleTariffPreview" v-if="hasTariffs && !isTariffLoading">
                                        <i class="material-icons">visibility</i>
                                    </button>
                                    <i class="material-icons tariff-mini-spin grey-text" v-if="isTariffLoading">autorenew</i>
                                    <button type="button" class="btn-flat tariff-card-icon-action" title="Refresh tariff items"
                                        @click.prevent="handleTariffReload" v-else>
                                        <i class="material-icons">refresh</i>
                                    </button>
                                </span>
                            </span>
                        </div>
                        <div class="col s12" v-if="tariffMessage && !isTariffLoading">
                            <p class="tariff-card-message">{{ tariffMessage }}</p>
                        </div>
                        <div class="input-field col s12 m6">
                            <TariffSearch :inputId="claimSearchInputId" :data="filterData" :startAgain="resetClaim"
                                :startEdit="editClaim" :disabled="isSubmitting || isTariffLoading || !hasTariffs" @selected="handlePick"
                                @searching="handleSearch" @clear="handleClear" />
                        </div>
                        <div class="input-field col s6 m3">
                            <input v-model="qty" @input="handlePricing" id="qty" type="number"
                                :disabled="isSubmitting" />
                            <label class="active" for="qty">Quantity</label>
                        </div>
                        <div class="input-field col s6 m3">
                            <input v-model="amt" id="amt" type="number" readonly :disabled="isSubmitting" />
                            <label class="active" for="amt">Amount Charged</label>
                        </div>
                        <div class="input-field col s12">
                            <a class="waves-effect waves-light btn-large black lighten-2 col s12 m2 left" :class="{ 'disabled': isSubmitting }"
                                @click.prevent="handleBack('clearClaim')">
                                <i class="medium material-icons left">chevron_left</i>Back
                            </a>

                             <!-- Breaker -->
                            <span class="co1 m7"></span>

                            <button class="waves-effect waves-light btn-large col s12 m3 right"
                                :disabled="!canAddClaim"
                                :class="{ 'disabled': !canAddClaim, 'teal': canAddClaim }"
                                @click.prevent="handleClaimItem">
                                <i class="medium material-icons left">check_circle</i>Add Claim Item
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <Teleport to="body">
        <div class="tariff-preview-overlay" @click.self="handleTariffPreviewClose" v-if="isTariffPreviewOpen">
            <section :id="tariffModalId" class="tariff-preview-modal" role="dialog" aria-modal="true" aria-label="Tariff items">
                <button type="button" class="tariff-preview-close" title="Close" @click.prevent="handleTariffPreviewClose">
                    <i class="material-icons">close</i>
                </button>
                <div class="tariff-preview-content">
                <h5>Tariff Items</h5>
                <div class="input-field tariff-preview-search">
                    <input v-model="tariffPreviewSearch" :id="`${tariffModalId}-search`" type="search" />
                    <label class="active" :for="`${tariffModalId}-search`">Search tariff item</label>
                </div>
                <ul class="collection tariff-preview-list" v-if="filteredTariffPreview.length">
                    <li class="collection-item" v-for="tariff in filteredTariffPreview" :key="`${tariff.item}-${tariff.price}`">
                        <span>{{ tariff.item }}</span>
                        <b class="secondary-content">&#8358;{{ formatMoney(tariff.price) }}</b>
                    </li>
                </ul>
                <p class="grey-text" v-else>No tariff item matches your search.</p>
            </div>
            <div class="tariff-preview-footer">
                <button type="button" class="btn-flat waves-effect waves-teal" @click.prevent="handleTariffPreviewClose">Close</button>
            </div>
            </section>
        </div>
    </Teleport>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import getItems from "@/composables/getItems";
import TariffSearch from "@/components/search/TariffSearch.vue";
import { urlFormat } from "@/composables/tariff/urlFormat";
import { getCache, addCache, removeCache } from "@/composables/cache/cache";
import { capitalizeFirstLetter } from "@/composables/fxLibrary/coreFx.js";

export default {
    name: "AddClaimItemComponent",
    emits: ["update"],
    props: ["turl", "editDiagnosis", "reset"],
    components: { TariffSearch },
    setup(props, context) {
        const responseDefault = ref([]);
        const responseSearch = ref([]);
        const isSubmitting = ref(props.reset);
        const error = ref(false);
        const diagnosis = ref(props.editDiagnosis);
        const editClaim = ref(null);
        const resetClaim = ref(false);
        const selectedItem = ref(null);
        const selectedPrice = ref(null);
        const claim = ref(null);
        const qty = ref(null);
        const amt = ref(null);
        const claimSearchInputId = `claimTariffItem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const tariffUrl = ref(null);
        const isTariffLoading = ref(false);
        const tariffError = ref(null);
        const tariffLoaded = ref(false);
        const tariffPreviewSearch = ref("");
        const isTariffPreviewOpen = ref(false);
        const tariffModalId = `claimTariffPreview-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const canAddClaim = computed(() => {
            return Boolean(selectedItem.value && qty.value && amt.value && !isSubmitting.value);
        });
        const hasTariffs = computed(() => responseDefault.value.length > 0);
        const tariffMessage = computed(() => {
            if (tariffError.value) {
                return tariffError.value;
            }
            if (tariffLoaded.value && !hasTariffs.value) {
                return "No tariff item is available for this scheme and provider.";
            }
            return null;
        });
        const filteredTariffPreview = computed(() => {
            const term = tariffPreviewSearch.value?.toLowerCase();
            if (!term) {
                return responseDefault.value;
            }
            return responseDefault.value.filter(x => x.item?.toLowerCase().includes(term));
        });

        // data tariff
        const filterData = computed(() => {
            // start
            let val = {};

            let valPlus;

            if (responseSearch.value) {
                valPlus = responseSearch.value.reduce((newArr, x) => {
                    newArr[x.item] = null;
                    return newArr;
                }, {});
                val = { ...val, ...valPlus };
            }

            // return final
            return val;
        });
     
      // search function
      const getTariffFromApi = async (table, force = false) => {
      if (!table) {
        tariffError.value = "Unable to load tariff items for this scheme and provider.";
        tariffLoaded.value = true;
        return;
      }
      tariffUrl.value = table;
      tariffError.value = null;
      // get item from store (cache)
      let cacheTariff = getCache(table);
      // get from api
      if (cacheTariff?.length && !force) {
        responseDefault.value = cacheTariff;
        tariffLoaded.value = true;
      } else {
          isTariffLoading.value = true;
          const { res, err } = await getItems(table);
          if (res.value) {
            // search
            responseDefault.value = res.value.data || [];
            // add cache
            addCache(table, responseDefault.value);
          } else {
            tariffError.value = err.value || "Unable to load tariff items for this scheme and provider.";
          }
          tariffLoaded.value = true;
          isTariffLoading.value = false;
      }
    };

        // starter (set loading spin)
        onMounted(() => {
            // tariff url
            let url = urlFormat(props.turl);
            getTariffFromApi(url);
        });

        const handleTariffReload = () => {
            if (!tariffUrl.value) {
                return;
            }
            removeCache(tariffUrl.value);
            getTariffFromApi(tariffUrl.value, true);
        };

        const handleTariffPreview = () => {
            tariffPreviewSearch.value = "";
            isTariffPreviewOpen.value = true;
        };

        const handleTariffPreviewClose = () => {
            isTariffPreviewOpen.value = false;
        };

        const formatMoney = (val) => {
            return Number(val || 0)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        // update reset
        const updateSubmit = (val) => {
            if (val) {
                isSubmitting.value = false;
            }
        }

        // handle diagnosis
        const handleDiagnosis = () => {
            if (diagnosis.value) {
                // format for saving
                const { fx } = capitalizeFirstLetter(diagnosis.value);
                // send back
                context.emit("update", {
                    id: "resetDiagnosis",
                    value: fx.value,
                });

                // keep buttons and fields disabled
                isSubmitting.value = true;

                // reset error (if required)
                error.value = false;
            } else {
                error.value = true;
            }
        };

        // load claim item
        const handleClaimItem = () => {
            // start action
            if (selectedItem.value && qty.value && amt.value) {
                // format for saving
                const { fx } = capitalizeFirstLetter(selectedItem.value);

                // send for claim add
                context.emit("update", {
                    id: "addNewClaim",
                    value: {
                        id: Date.now(),
                        claim: fx.value,
                        qty: qty.value,
                        amt: amt.value,
                        standard: Number(selectedPrice.value).toFixed(2),
                    },
                });

                // keep buttons and fields disabled
                isSubmitting.value = true;

                // clear entry
                // claimInputClear();

                // reset error (if required)
                error.value = false;
            } else {
                error.value = true;
            }

        };

        // clear (back)
        const handleBack = (val) => {
            // clear
            context.emit("update", {
                id: val,
                value: true
            });
        }

        // watch qty & price
        const handlePricing = () => {
            amt.value = (Number(qty.value) * Number(selectedPrice.value)).toFixed(2);
        };

        // filter components
        const handleSearch = (val) => {
            if (val.item) {
                // sort new search
                responseSearch.value = responseDefault.value.filter((x) =>
                    x.item.toLowerCase().includes(val.item.toLowerCase())
                );
            } else {
                // empty search array
                responseSearch.value = [];

                // clear entry
                claimInputClear();
            }
        };

        // null reset
        const handleClear = (val) => {
            resetClaim.value = val.item;
            editClaim.value = val.item;
        };

        // get option
        const handlePick = (val) => {
            //set
            selectedItem.value = val.item;

            //set
            if (selectedItem.value) {
                // get the price out
                responseSearch.value.forEach((x) => {
                    if (x.item === selectedItem.value) {
                        // found
                        selectedPrice.value = x.price;
                    }
                });

                // set quantity & amount
                qty.value = 1;
                amt.value = (Number(qty.value) * Number(selectedPrice.value)).toFixed(
                    2
                );
            } else {
                // if null (very unlikely)
                // clear entry
                claimInputClear();
            }
        };

        // clear inputs
        const claimInputClear = () => {
            // clear all
            selectedItem.value = null;
            selectedPrice.value = null;
            resetClaim.value = true; // will clear claims input
            qty.value = null;
            amt.value = null;
            responseSearch.value = [];

            // clear error (if needed)
            error.value = false;
        };

        // watch for changes
        watch(props, (newVal) => {
            updateSubmit(newVal.reset);
        });

        return {
            handleDiagnosis,
            handleClaimItem,
            handleBack,
            handleTariffReload,
            handleTariffPreview,
            handleTariffPreviewClose,
            handlePricing,
            handleSearch,
            handleClear,
            handlePick,
            isSubmitting,
            canAddClaim,
            selectedItem,
            selectedPrice,
            responseSearch,
            editClaim,
            resetClaim,
            filterData,
            claimSearchInputId,
            filteredTariffPreview,
            formatMoney,
            hasTariffs,
            isTariffLoading,
            isTariffPreviewOpen,
            tariffMessage,
            tariffModalId,
            tariffPreviewSearch,
            error,
            diagnosis,
            claim,
            qty,
            amt,
        };
    },
};
</script>

<style scoped>
.add-claim-item-card,
.add-claim-item-card .card-content,
.add-claim-item-card form,
.add-claim-item-card .row,
.add-claim-item-card .input-field {
    overflow: visible;
}

.add-claim-item-card {
    position: relative;
    z-index: 20;
}

.add-claim-item-card :deep(.autocomplete-content.dropdown-content) {
    max-height: 320px;
    overflow-y: auto;
    z-index: 1200;
}

.tariff-card-icon-action {
    height: 32px;
    line-height: 32px;
    padding: 0 6px;
}

.tariff-card-actions {
    align-items: center;
    display: inline-flex;
    gap: 4px;
}

.tariff-card-icon-action i,
.tariff-preview-top-action i,
.tariff-mini-spin {
    color: #607d8b;
}

.tariff-preview-top-action {
    background: #eceff1;
    border-radius: 16px;
}

.tariff-mini-spin {
    animation: tariff-spin 0.9s linear infinite;
    display: inline-block;
}

.tariff-card-message {
    background: #fff8e1;
    border-radius: 6px;
    color: #6d4c41;
    margin: 8px 0 0;
    padding: 10px 12px;
}

.tariff-preview-overlay {
    background: rgba(38, 50, 56, 0.48);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1200;
}

.tariff-preview-modal {
    background: #fff;
    bottom: 32px;
    box-shadow: 0 18px 45px rgba(38, 50, 56, 0.24);
    display: flex;
    flex-direction: column;
    left: max(36px, calc((100vw - 1180px) / 2));
    position: fixed;
    right: max(36px, calc((100vw - 1180px) / 2));
    top: 104px;
    z-index: 1201;
}

.tariff-preview-content {
    flex: 1;
    overflow: hidden;
    padding: 32px 36px 20px;
}

.tariff-preview-search {
    margin-bottom: 22px;
    margin-top: 22px;
}

.tariff-preview-list {
    max-height: calc(100vh - 330px);
    overflow-y: auto;
}

.tariff-preview-close {
    background: #eceff1;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    height: 36px;
    position: absolute;
    right: 24px;
    top: 22px;
    width: 36px;
}

.tariff-preview-close i {
    color: #455a64;
    line-height: 36px;
}

.tariff-preview-footer {
    border-top: 1px solid #e0e0e0;
    padding-left: 36px;
    padding-right: 36px;
    text-align: right;
}

@media only screen and (max-width: 600px) {
    .tariff-preview-modal {
        bottom: 12px;
        left: 12px;
        right: 12px;
        top: 76px;
    }

    .tariff-preview-content {
        padding: 24px 18px 16px;
    }

    .tariff-preview-list {
        max-height: calc(100vh - 292px);
    }
}

@keyframes tariff-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
